import { format, differenceInDays, differenceInMonths, differenceInYears } from 'date-fns';
import { statusDotColours } from '../../styles/GlobalStyles';

// Property type formatting
export const propertyTypeFormat = (propertyType) => {
	return propertyType === "Single%20Family" ? "Single Family" : propertyType === "CONDO" ? "Condo" : propertyType === "SFR" ? "Single Family" : propertyType === "MFR" ? "Multi Family" : propertyType === "TWNH" ? "Townhouse" : propertyType;
};

export const propertyTypeFormatAbbreviated = (propertyType) => {
	return propertyType === "Single%20Family" ? "Single Family" : propertyType === "CONDO" ? "Con" : propertyType === "SFR" ? "SFH" : propertyType === "MFR" ? "MFH" : propertyType === "TWNH" ? "Tow" : propertyType;
};

// Date formatting
export const formatDaysOnMarket = (date) => {
	if ( date === null ) {
		return "N/A";
	}
	const daysSinceToday = differenceInDays(new Date(), new Date(date));
	if ( daysSinceToday >= 730 ) {
		// Over 2 years ago
		const formattedDate = format(new Date(date), "MMM dd, yyyy");
		return formattedDate;
	}
	else if ( daysSinceToday >= 365 ) {
		// 1 year or over (but under 2 years, handled above)
		const yearsDifference = differenceInYears(new Date(), new Date(date));
		const monthsRemainder = differenceInMonths(new Date(), new Date(date)) - (yearsDifference * 12);
		const formattedDate = `${yearsDifference} year${yearsDifference === 1 ? "" : "s"}${monthsRemainder > 0 ? ` ${monthsRemainder} month${monthsRemainder === 1 ? "" : "s"}` : ""} ago`;
		return formattedDate;
	}
	else if ( daysSinceToday > 99 ) {
		// 100 days or over
		const monthsDifference = differenceInMonths(new Date(), new Date(date));
		const formattedDate = `${monthsDifference} month${monthsDifference === 1 ? "" : "s"} ago`;
		return formattedDate;
	}
	else {
		// Under 100 days
		const formattedDate = `${daysSinceToday} day${daysSinceToday === 1 ? "" : "s"} ago`;
		return formattedDate;
	}
};

// Price utilities
export const getPriceField = (property) => {
	if ( property.listingDetails.listingStatus === "for_sale" ) {
		return property.listingDetails.listingPrice;
	}
	else if ( property.listingDetails.listingStatus === "pending" ) {
		return property.listingDetails.listingPrice;
	}
	else {
		return property.listingDetails.soldListingPrice;
	}
};

// Property object generation
export const generateSubjectPropertyObject = async(element) => {
	const constructionType = element.propertyAttributes.constructionTypeBySource === undefined || element.propertyAttributes.constructionTypeBySource === null || element.propertyAttributes.constructionTypeBySource.reapi === null ? element.propertyAttributes.constructionType : element.propertyAttributes.constructionTypeBySource.reapi;
	const subjectPropertySubmission = {
		propertyInfo: {
			propertyType: propertyTypeFormat(element.propertyAttributes.propertyType),
			bedrooms: element.propertyAttributes.bedrooms,
			bathrooms: element.propertyAttributes.bathrooms,
			sqFootage: element.propertyAttributes.livingSquareFootage,
			yearBuilt: element.propertyAttributes.yearBuilt,
			lotSize: element.propertyAttributes.lotSquareFootage,
			garage: element.propertyAttributes.garage,
			garageSpaces: element.garageSpaces !== undefined && element.garageSpaces !== null ? element.garageSpaces : null,
			garageAttachment: element.garageType === "attached" || element.garageType === "detached" ? element.garageType : null,
			pool: element.propertyAttributes.pool,
			subdivision: element.propertyAttributes.subdivision,
			waterfront: element.propertyAttributes.waterFront,
			constructionType: constructionType === "" ? "Unknown" : constructionType,
			censusBlock: element.addressDetails.censusBlock,
			censusTract: element.addressDetails.geoId,
			stories: element.propertyAttributes.stories,
			adu: element.listingDetails.aduFlag ? true : false,
			architecturalStyle: element.propertyAttributes?.architecturalStyle ?? null,
			carport: element.propertyAttributes?.carport ?? false,
			carportSpaces: element.propertyAttributes?.carportSpaces ?? null
		},
		status: element.listingDetails.listingStatus === "for_sale" ? "For sale" : element.listingDetails.listingStatus === "pending" ? "Pending" : "Sold",
		address: `${element.addressDetails.streetAddress}, ${element.addressDetails.city}, ${element.addressDetails.state} ${element.addressDetails.postalCode}`,
		latitude: element.addressDetails.latitude,
		longitude: element.addressDetails.longitude
	};

	if ( element.listingDetails.listingStatus === "for_sale" || element.listingDetails.listingStatus === "pending" ) {
		subjectPropertySubmission.listingPrice = element.listingDetails.listingPrice;
		subjectPropertySubmission.originalListingPrice = element.listingDetails.saleListingPrice;
		subjectPropertySubmission.totalPriceChanges = element.listingDetails.totalPriceChanges;
		subjectPropertySubmission.listingDate = element.listingDetails.saleListingDate;
	}
	else if ( element.listingDetails.soldListingPrice !== null ) {
		subjectPropertySubmission.lastSoldDate = element.listingDetails.soldListingDate;
		subjectPropertySubmission.lastSoldPrice = element.listingDetails.soldListingPrice;
	}

	if ( element.propertyAttributes.floodZoneType !== undefined && element.propertyAttributes.floodZoneType !== null ) {
		subjectPropertySubmission.floodZoneType = element.propertyAttributes.floodZoneType;
	}
	else {
		subjectPropertySubmission.floodZoneType = "D";
	}

	return subjectPropertySubmission;
};

export const generatePropertyObject = async(element) => {
	const propertyType = element.propertyAttributes.propertyType;
	const flipStatus = element.compDetails.flop === true ? "Flop" : element.compDetails.flip === true ? "Flip" : "Non-flip";
	const price = getPriceField(element)
	const recency = element.listingDetails.listingStatus === "for_sale" || element.listingDetails.listingStatus === "pending" ? `${element.novationDOM} days` : formatDaysOnMarket(element.listingDetails.soldListingDate);
	const constructionType = element.propertyAttributes.constructionTypeBySource === undefined || element.propertyAttributes.constructionTypeBySource === null || element.propertyAttributes.constructionTypeBySource.reapi === null ? element.propertyAttributes.constructionType : element.propertyAttributes.constructionTypeBySource.reapi;
	const compInfo = {
		propertyInfo: {
			propertyType: propertyTypeFormat(propertyType),
			bedrooms: element.propertyAttributes.bedrooms,
			bathrooms: element.propertyAttributes.bathrooms,
			sqFootage: element.propertyAttributes.livingSquareFootage,
			yearBuilt: element.propertyAttributes.yearBuilt,
			lotSize: element.propertyAttributes.lotSquareFootage,
			garage: element.propertyAttributes.garage,
			garageSpaces: element.propertyAttributes.garageSpaces ?? null,
			garageAttachment: element.propertyAttributes.garageAttachment ?? null,
			pool: element.propertyAttributes.pool,
			listingDescription: element.listingDetails.listingDescriptionValidFlag === false ? "" : element.listingDetails.listingDescription,
			subdivision: element.propertyAttributes.subdivision,
			waterfront: element.propertyAttributes.waterFront,
			constructionType: constructionType === "" ? "Unknown" : constructionType,
			censusBlock: element.addressDetails.censusBlock,
			censusTract: element.addressDetails.geoId,
			stories: element.propertyAttributes.stories,
			adu: element.listingDetails.aduFlag,
			aboveGradeFinishedArea: element.propertyAttributes?.aboveGradeFinishedArea ?? null,
			belowGradeFinishedArea: element.propertyAttributes?.belowGradeFinishedArea ?? null,
			basement: element.propertyAttributes?.basement ?? null,
			architecturalStyle: element.propertyAttributes?.architecturalStyle ?? null,
			carport: element.propertyAttributes?.carport ?? false,
			carportSpaces: element.propertyAttributes?.carportSpaces ?? null
		},
		distanceMiles: element.compDetails.distanceMiles,
		highwayObstacle: element.compDetails.highway,
		waterObstacle: element.compDetails.water,
		railroadObstacle: element.compDetails.railroad,
		condition: element.compDetails.propertyCondition,
		flipStatus: flipStatus,
		status: element.listingDetails.listingStatus === "for_sale" ? "For sale" : element.listingDetails.listingStatus === "pending" ? "Pending" : "Sold",
		daysOnMarket: recency,
		daysSinceSold: element.listingDetails.soldListingDate ? differenceInDays(new Date(), new Date(element.listingDetails.soldListingDate)) : 0,
		price: price,
		pricePerSqFt: element.propertyAttributes.livingSquareFootage === null || element.propertyAttributes.livingSquareFootage === 0 ? 0 : price / element.propertyAttributes.livingSquareFootage,
		scores: {
			overall: element.compDetails.overallScore,
			location: element.compDetails.locationScore,
			recency: element.compDetails.recencyScore,
			similarity: element.compDetails.similarityScore
		},
		address: `${element.addressDetails.streetAddress}, ${element.addressDetails.city}, ${element.addressDetails.state} ${element.addressDetails.postalCode}`,
		latitude: element.addressDetails.latitude,
		longitude: element.addressDetails.longitude,
		zpid: element.uniqueIds.zillowId
	};

	if ( element.propertyAttributes.floodZoneType !== undefined && element.propertyAttributes.floodZoneType !== null ) {
		compInfo.floodZoneType = element.propertyAttributes.floodZoneType;
	}
	else {
		compInfo.floodZoneType = "D";
	}

	return compInfo;
};

// Property styling utilities
export const propertyConditionBG = (condition, darkMode) => {
	if ( condition === "Recently Remodeled" ) {
		return "bg-colour-green-02";
	}
	else if ( condition === "Well-Maintained – Contemporary" ) {
		return "bg-colour-aqua-02";
	}
	else if ( condition === "Well-Maintained – Traditional" ) {
		return "bg-colour-yellow-02";
	}
	else if ( condition === "Moderate Rehab" ) {
		return "bg-colour-orange-02";
	}
	else if ( condition === "Gut Rehab" ) {
		return "bg-colour-red-02";
	}
	else {
		return "";	
	}
};

export const propertyStatusBG = (property, darkMode) => {
	if ( property.listingDetails.listingStatus === "for_sale" ) {
		return darkMode ? "bg-colour-for-sale-dark" : "bg-colour-for-sale";
	}
	else if ( property.listingDetails.listingStatus === "pending" ) {
		return darkMode ? "bg-colour-pending-dark" : "bg-colour-pending";
	}
	else {
		return darkMode ? "bg-colour-sold-dark" : "bg-colour-sold";
	}
};

export const propertyStatusText = (property) => {
	if ( property.listingDetails.listingStatus === "for_sale" ) {
		return "For sale";
	}
	else if (
		property?.listingDetails?.listingStatus === "pending" &&
		property?.listingDetails?.acceptingBackupOffersFlag === true
	) {
		return "Backup offers";
	}
	else if (
		property?.listingDetails?.listingStatus === "pending" &&
		property?.listingDetails?.contingentFlag === true
	) {
		return "Contingent";
	}
	else if ( property.listingDetails.listingStatus === "pending" ) {
		return "Pending";
	}
	else {
		return "Sold";
	}
};

export const getStatusDotColour = (property) => {
	if ( property.listingDetails.listingStatus === "for_sale" ) {
		return statusDotColours.forSale;
	}
	else if ( property.listingDetails.listingStatus === "pending" ) {
		return statusDotColours.pending;
	}
	else {
		return statusDotColours.sold;
	}
};

